import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Image,
  Switch,
  Tag,
  Tooltip,
  Button,
  Popover,
  Form,
  ConfigProvider,
} from "antd";
import { RiAddBoxFill, RiRefreshLine, RiTeamFill } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import {
  SearchOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  DeleteOutlined,
  SettingOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import {
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import {
  deleteDeveloper,
  getallDeveloper,
  updateDeveloper,
  deleteDocumentImage,
  updateprofile,
  getallSoftDeletedDeveloper,
  hardDeleteDeveloper,
} from "../../utils/API";
import { hasPermission } from "../../utils/HasPermission";
// import "antd/dist/reset.css";
import AddDeveloperModal from "./Add";
import EditDeveloperModal from "./Edit";
import dayjs from "dayjs";
import { JSX } from "react/jsx-runtime";
import { AlignCenter } from "lucide-react";
import { MdOutlineRestore } from "react-icons/md";
import { RiInboxArchiveLine } from "react-icons/ri";
import { FcMoneyTransfer } from "react-icons/fc";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

// Define the Developer interface based on API response
interface Developer {
  _id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  mobileNo: string;
  dob: string;
  employmentType?: number; // Added: 1 = Employee, 0 = Trainee
  joining_date: string;
  exit_date: string;
  leaveBalance: number;
  city: string;
  photo: string;
  designation: { name: string };
  isActive: boolean;
  isOwner: boolean;
  isWebsite: boolean;
  is_exclude?: boolean;
  isPlatform: boolean;
  document: string[];
}

export const DevelopersTable = () => {
  const [dataSource, setDataSource] = useState<Developer[]>([]);
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/developers/";
  const [filteredData, setFilteredData] = useState<Developer[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [hardDeleteId, setHardDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  // const [selectedRecord, setDeveloper] = useState<any>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<Developer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false); // Add this line
  const [selectedRecord, setSelectedRecord] = useState<Developer | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const navigate = useNavigate();
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [form] = Form.useForm();
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletedDevelopers, setDeletedDevelopers] = useState<Developer[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("1");
  const [filteredDeleted, setFilteredDeleted] = useState<Developer[]>([]);

  // fetch soft deleted developers
  const fetchDeletedDevelopers = async () => {
    setDeletedLoading(true);
    try {
      const response: any = await getallSoftDeletedDeveloper(
        searchText,
        deletedPage,
      );

      if (response?.data && Array.isArray(response.data)) {
        setDeletedDevelopers(response.data);
        setDeletedTotal(response.totalCount || response.data.length);
      } else {
        setDeletedDevelopers([]);
        setDeletedTotal(0);
      }
    } catch (error) {
      message.error("Failed to fetch deleted developers");
    } finally {
      setDeletedLoading(false);
    }
  };

  // Fetch Developers from API
  const fetchDevelopers = async () => {
    setLoading(true);
    try {
      const response: any = await getallDeveloper(searchText, page);

      // Debug log to check employmentType
      if (response?.data && response.data.length > 0) {
        console.log("First developer data:", response.data[0]);
        console.log("Employment Type value:", response.data[0]?.employmentType);
      }

      // If we're searching, reset to page 1
      if (searchText && page !== 1) {
        setPage(1);
      }

      setTotalRecords(response.totalCount || response.data.length);

      if (response?.data && Array.isArray(response.data)) {
        setDataSource(response.data);
        setFilteredData(response.data);
      } else {
        setDataSource([]);
        setTotalRecords(0);
        message.error(response.message || "Failed to fetch developers");
      }
    } catch (error) {
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount & when searchText changes
  useEffect(() => {
    if (activeTab === "1") {
      fetchDevelopers();
    }
  }, [searchText, page, activeTab]);

  useEffect(() => {
    if (activeTab === "2") {
      fetchDeletedDevelopers();
    }
  }, [searchText, deletedPage, activeTab]);

  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredDeleted(deletedDevelopers);
    } else {
      const lower = searchText.toLowerCase();
      const filtered = deletedDevelopers.filter(
        (dev) =>
          dev.employeeId?.toLowerCase().includes(lower) ||
          dev.username?.toLowerCase().includes(lower) ||
          dev.email?.toLowerCase().includes(lower) ||
          dev.designation?.name?.toLowerCase().includes(lower) ||
          dev.city?.toLowerCase().includes(lower) ||
          dev.mobileNo?.includes(searchText),
      );
      setFilteredDeleted(filtered);
    }
  }, [searchText, deletedDevelopers]);

  const handleDeleteDocument = async (doc: string, index: number) => {
    if (!selectedRecord) {
      message.error("No record selected");
      return;
    }

    try {
      if (typeof doc === "string") {
        const documentName = doc.split("/").pop() || "";

        await deleteDocumentImage(selectedRecord._id, documentName);
      }

      const updatedDocuments = [...(selectedRecord.document || [])];
      updatedDocuments.splice(index, 1);

      setSelectedRecord({
        ...selectedRecord,
        document: updatedDocuments,
      });

      message.success("Document deleted successfully");
    } catch (error) {
      console.error("Error deleting document:", error);
      message.error("Failed to delete document");
    }
  };

  // Handle Delete Confirmation
  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleHardDeleteConfirmation = (id: string) => {
    setHardDeleteId(id);
  };

  const handleRestoreConfirmation = (id: string) => {
    setRestoreId(id);
  };

  // Handle Delete Action
  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response = await deleteDeveloper(deleteId, true);
        if (response) {
          message.success("Developer move to archive successfully");
          fetchDevelopers(); // Refresh list
          fetchDeletedDevelopers();
        } else {
          message.error("Failed to move developer");
        }
      } catch (error) {
        message.error("Error moveing developer to archive. Please try again.");
      } finally {
        setDeleteId(null); // Close the confirmation modal
      }
    }
  };

  const handleHardDelete = async () => {
    if (hardDeleteId) {
      try {
        const response = await hardDeleteDeveloper(hardDeleteId);
        if (response) {
          message.success("Developer deleted successfully");
          fetchDevelopers(); // Refresh list
          fetchDeletedDevelopers();
        } else {
          message.error("Failed to delete developer");
        }
      } catch (error) {
        message.error("Error deleting developer. Please try again.");
      } finally {
        setHardDeleteId(null); // Close the confirmation modal
      }
    }
  };

  const handleToggleStatus = async (
    developerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append("id", developerId);
      formData.append("isActive", String(newStatus)); //  Ensure isActive is explicitly sent

      await updateDeveloper(formData); //  Send proper payload
      message.success(
        `Developer ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isActive: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isActive: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating role status:", error);
      message.error("Failed to update role status.");
    }
  };
  // Toggle the developer-level "exclude from lunch break" flag
  const handleToggleExclude = async (
    developerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append("id", developerId);
      formData.append("is_exclude", String(newStatus));

      await updateDeveloper(formData);
      message.success(
        `Developer ${newStatus ? "excluded from" : "included in"} lunch break cycle!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, is_exclude: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, is_exclude: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating break exclusion:", error);
      message.error("Failed to update break exclusion.");
    }
  };
  const handleOwnerStatus = async (
    developerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append("id", developerId);
      formData.append("isOwner", String(newStatus)); //  Ensure isActive is explicitly sent

      await updateDeveloper(formData); //  Send proper payload
      message.success(
        `Developer ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isOwner: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isOwner: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating role status:", error);
      message.error("Failed to update role status.");
    }
  };
  const handleWebsiteStatus = async (
    developerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append("id", developerId);
      formData.append("isWebsite", String(newStatus)); //  Ensure isActive is explicitly sent

      await updateDeveloper(formData); //  Send proper payload
      message.success(
        `Developer ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isWebsite: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isWebsite: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating role status:", error);
      message.error("Failed to update role status.");
    }
  };
  const handlePlatformStatus = async (
    developerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append("_id", developerId);
      formData.append("isPlatform", String(newStatus)); //  Ensure isActive is explicitly sent

      await updateprofile(formData); //  Send proper payload
      message.success(
        `Developer ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isPlatform: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === developerId ? { ...role, isPlatform: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating role status:", error);
      message.error("Failed to update role status.");
    }
  };
  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    // Refresh your task list or perform other actions
    fetchDevelopers(); // Example: refresh the task list
  };
  const handleEditClick = (record: Developer) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
  };

  const openPasswordModal = (user: any) => {
    setSelectedUser(user);
    setIsPasswordModalVisible(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalVisible(false);
    setSelectedUser(null);
    form.resetFields();
  };

  const onFinishPasswordChange = async (values: any, user: any) => {
    setIsUpdating(true);
    try {
      if (values.newPassword !== values.confirmPassword) {
        throw new Error("New password and confirm password do not match");
      }

      const formData = new FormData();
      formData.append("_id", user._id || "");
      formData.append("newPassword", values.newPassword);

      const response = await updateprofile(formData);

      if (response) {
        message.success("Password updated successfully!");
        form.resetFields();
        closePasswordModal();
      }
    } catch (error: any) {
      console.error("Password update error:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update password",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const columns = [
    {
      title: "Employee ID",
      dataIndex: "employeeId",
      key: "employeeId",
      fixed: "left" as const,
      render: (text: string) => text || "-",
      sorter: (a: Developer, b: Developer) => {
        const empA = a.employeeId || "";
        const empB = b.employeeId || "";
        return empA.localeCompare(empB);
      },
      defaultSortOrder: "ascend" as const,
    },
    {
      title: "Photo",
      dataIndex: "photo",
      fixed: "left" as const,
      render: (text: string) => (
        <Image
          src={
            text
              ? `${IMAGEURL}${text}`
              : `${process.env.PUBLIC_URL}/download.png`
          }
          width={50}
          height={50}
          className="rounded-full"
          fallback={`${process.env.PUBLIC_URL}/download.png`}
          onError={(e) => {
            e.currentTarget.src = `${process.env.PUBLIC_URL}/download.png`;
          }}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "username",
      fixed: "left" as const,
      render: (text: string) => text || "-",
      sorter: (a: Developer, b: Developer) => {
        const nameA = a.username || "";
        const nameB = b.username || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (designation: { name: string } | undefined) => {
        if (!designation?.name) return "-";
        const name = designation.name.trim();
        return (
          <Tag
            style={{
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              color: "#fff",
              padding: "1px 10px",
              borderRadius: "5px",
              fontSize: "12px",
              border: "none",
            }}
          >
            {name}
          </Tag>
        );
      },
      sorter: (a: Developer, b: Developer) => {
        const desigA = a.designation?.name || "";
        const desigB = b.designation?.name || "";
        return desigA.localeCompare(desigB);
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text: string) => text || "-",
      sorter: (a: Developer, b: Developer) => {
        const emailA = a.email || "";
        const emailB = b.email || "";
        return emailA.localeCompare(emailB);
      },
    },
    {
      title: "Mobile No",
      dataIndex: "mobileNo",
      render: (text: string) => text || "-",
      sorter: (a: Developer, b: Developer) => {
        const mobileA = a.mobileNo || "";
        const mobileB = b.mobileNo || "";
        return mobileA.localeCompare(mobileB);
      },
    },
    {
      title: "DOB",
      dataIndex: "dob",
      render: (text: string) => {
        if (!text) return "-";
        let parsed = dayjs(text, "DD-MM-YYYY", true);
        if (!parsed.isValid()) {
          const date = new Date(text);
          if (!isNaN(date.getTime())) {
            return dayjs(date).format("DD-MM-YYYY");
          }
          return "-";
        }
        return parsed.format("DD-MM-YYYY");
      },
      sorter: (a: Developer, b: Developer) => {
        const dateA = dayjs(a.dob, "DD-MM-YYYY").isValid()
          ? dayjs(a.dob, "DD-MM-YYYY").valueOf()
          : new Date(a.dob || 0).getTime();
        const dateB = dayjs(b.dob, "DD-MM-YYYY").isValid()
          ? dayjs(b.dob, "DD-MM-YYYY").valueOf()
          : new Date(b.dob || 0).getTime();
        return dateA - dateB;
      },
    },
    // NEW COLUMN: Employment Type (between DOB and Joining Date)
    // In the columns array, update the Employment Type column:
    {
      title: "Employment Type",
      dataIndex: "employmentType",
      key: "employmentType",
      render: (value: number | undefined) => {
        if (value === undefined || value === null) {
          return (
            <Tag
              color="default"
              style={{
                borderRadius: "5px",
                fontSize: "12px",
                padding: "2px 10px",
              }}
            >
              Not Set
            </Tag>
          );
        }
        return value === 1 ? (
          <Tag
            color="blue"
            style={{
              borderRadius: "5px",
              fontSize: "12px",
              padding: "2px 10px",
            }}
          >
            Full Time
          </Tag>
        ) : (
          <Tag
            color="orange"
            style={{
              borderRadius: "5px",
              fontSize: "12px",
              padding: "2px 10px",
            }}
          >
            Trainee
          </Tag>
        );
      },
      sorter: (a: Developer, b: Developer) => {
        const typeA = a.employmentType ?? 0;
        const typeB = b.employmentType ?? 0;
        return typeA - typeB;
      },
    },
    {
      title: "Joining Date",
      dataIndex: "joining_date",
      render: (text: string) => {
        if (!text) return "-";
        let parsed = dayjs(text, "DD-MM-YYYY", true);
        if (!parsed.isValid()) {
          const date = new Date(text);
          if (!isNaN(date.getTime())) {
            return dayjs(date).format("DD-MM-YYYY");
          }
          return "-";
        }
        return parsed.format("DD-MM-YYYY");
      },
      sorter: (a: Developer, b: Developer) => {
        const dateA = dayjs(a.joining_date, "DD-MM-YYYY").isValid()
          ? dayjs(a.joining_date, "DD-MM-YYYY").valueOf()
          : new Date(a.joining_date || 0).getTime();
        const dateB = dayjs(b.joining_date, "DD-MM-YYYY").isValid()
          ? dayjs(b.joining_date, "DD-MM-YYYY").valueOf()
          : new Date(b.joining_date || 0).getTime();
        return dateA - dateB;
      },
    },
    {
      title: "Exit Date",
      dataIndex: "exit_date",
      render: (text: string) => {
        if (!text) return "-";
        let parsed = dayjs(text, "DD-MM-YYYY", true);
        if (!parsed.isValid()) {
          const date = new Date(text);
          if (!isNaN(date.getTime())) {
            return dayjs(date).format("DD-MM-YYYY");
          }
          return "-";
        }
        return parsed.format("DD-MM-YYYY");
      },
      sorter: (a: Developer, b: Developer) => {
        const dateA = dayjs(a.exit_date, "DD-MM-YYYY").isValid()
          ? dayjs(a.exit_date, "DD-MM-YYYY").valueOf()
          : new Date(a.exit_date || 0).getTime();
        const dateB = dayjs(b.exit_date, "DD-MM-YYYY").isValid()
          ? dayjs(b.exit_date, "DD-MM-YYYY").valueOf()
          : new Date(b.exit_date || 0).getTime();
        return dateA - dateB;
      },
    },
    {
      title: "City",
      dataIndex: "city",
      render: (text: string) => text || "-",
      sorter: (a: Developer, b: Developer) => {
        const cityA = a.city || "";
        const cityB = b.city || "";
        return cityA.localeCompare(cityB);
      },
    },
    {
      title: "Leave Balance",
      dataIndex: "leaveBalance",
      render: (text: number) => text || "0",
      sorter: (a: Developer, b: Developer) => {
        const balanceA = a.leaveBalance || 0;
        const balanceB = b.leaveBalance || 0;
        return balanceA - balanceB;
      },
    },
    {
      title: "Exclude Break",
      dataIndex: "is_exclude",
      width: 110,
      align: "center" as const,
      render: (value: boolean, record: Developer) => (
        <div className="flex h-full w-full items-center justify-center">
          <Tooltip
            title={
              value
                ? "Excluded from 30-min lunch break cycle"
                : "Included in 30-min lunch break cycle"
            }
          >
            <ConfigProvider theme={{ token: { colorPrimary: "#001529" } }}>
              <Switch
                checked={value === true}
                size="small"
                disabled={!hasPermission("developers", "update")}
                onChange={() => handleToggleExclude(record._id, value === true)}
              />
            </ConfigProvider>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Reset Password",
      dataIndex: "resetPassword",
      render: (_: any, record: any) => (
        <div className="flex h-full w-full items-center justify-center">
          <Tooltip title="Reset Password">
            <Button
              type="default"
              shape="circle"
              icon={<KeyOutlined />}
              onClick={() => openPasswordModal(record)}
              className="flex h-9 w-9 items-center justify-center rounded-full 
            bg-sky-100 text-sky-600 shadow
            hover:bg-sky-200 transition"
              style={{
                background: "linear-gradient(135deg, #e0f2fe, #f0f9ff)",
                color: "#0284c7",
                border: "none",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Document",
      key: "document",
      render: (_: unknown, record: Developer) => (
        <div className="flex h-full w-full items-center justify-center">
          <Tooltip title="View Document">
            <button
              onClick={(e) => {
                e.preventDefault();
                setSelectedRecord(record);
                setIsModalOpen(true);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full 
            bg-sky-100 text-sky-600 shadow
            hover:bg-sky-200 transition"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="group-hover:scale-110 transition-transform"
              >
                <path
                  d="M6 4V20M18 4V20M6 4H18M6 20H18M9 8H15M9 12H15M9 16H13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  const getActionColumn = (isDeletedTab: boolean) => ({
    title: "Action",
    dataIndex: "action",
    render: (_: unknown, record: Developer) => {
      // 🔁 DELETED DEVELOPERS TAB
      if (isDeletedTab) {
        return (
          <div className="d-flex" style={{ gap: "8px" }}>
            <Tooltip title="Restore">
              <button
                onClick={() => handleRestoreConfirmation(record._id)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 
               shadow-[0_4px_12px_rgba(16,185,129,0.3)]
               hover:shadow-[0_6px_16px_rgbargba(16,185,129,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
              >
                <MdOutlineRestore size={18} />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                onClick={() => handleHardDeleteConfirmation(record._id)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
              >
                <FaTrashAlt size={12} />
              </button>
            </Tooltip>
          </div>
        );
      }

      const content = (
        <div
          style={{
            minWidth: 220,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              marginBottom: -6,
              color: "#1677ff",
            }}
          >
            Manage Settings
          </div>

          {/* Status Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Status</span>
            <label
              style={{
                position: "relative",
                display: "inline-block",
                width: 46,
                height: 24,
                backgroundColor: record.isActive ? "#1677ff" : "#ddd",
                borderRadius: 20,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <input
                type="checkbox"
                checked={record.isActive}
                onChange={() => handleToggleStatus(record._id, record.isActive)}
                style={{ display: "none" }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: record.isActive ? "calc(100% - 22px)" : 2,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "left 0.3s ease",
                }}
              />
            </label>
          </div>

          {/* Lead Owner Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Lead Owner</span>
            <label
              style={{
                position: "relative",
                display: "inline-block",
                width: 46,
                height: 24,
                backgroundColor: record.isOwner ? "#52c41a" : "#ddd",
                borderRadius: 20,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <input
                type="checkbox"
                checked={record.isOwner}
                onChange={() => handleOwnerStatus(record._id, record.isOwner)}
                style={{ display: "none" }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: record.isOwner ? "calc(100% - 22px)" : 2,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "left 0.3s ease",
                }}
              />
            </label>
          </div>
          {/* Website Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Website</span>
            <label
              style={{
                position: "relative",
                display: "inline-block",
                width: 46,
                height: 24,
                backgroundColor: record.isWebsite ? "#6366f1" : "#ddd",
                borderRadius: 20,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <input
                type="checkbox"
                checked={record.isWebsite}
                onChange={() =>
                  handleWebsiteStatus(record._id, record.isWebsite)
                }
                style={{ display: "none" }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: record.isWebsite ? "calc(100% - 22px)" : 2,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "left 0.3s ease",
                }}
              />
            </label>
          </div>
          {/* Platform  */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Platform</span>
            <label
              style={{
                position: "relative",
                display: "inline-block",
                width: 46,
                height: 24,
                backgroundColor: record.isPlatform ? "#094782" : "#ddd",
                borderRadius: 20,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <input
                type="checkbox"
                checked={record.isPlatform}
                onChange={() =>
                  handlePlatformStatus(record._id, record.isPlatform)
                }
                style={{ display: "none" }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: record.isPlatform ? "calc(100% - 22px)" : 2,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "left 0.3s ease",
                }}
              />
            </label>
          </div>
        </div>
      );

      // ✅ ACTIVE DEVELOPERS TAB (your existing UI)
      return (
        <div className="flex h-full w-full items-center justify-center gap-2">
          {/* ⚙️ Settings */}
          <Popover content={content} trigger="click" placement="bottomRight">
            <Tooltip title="Settings">
              <button
                className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
              >
                <SettingOutlined />
              </button>
            </Tooltip>
          </Popover>

          {/* ✏️ Edit */}
          {hasPermission("developers", "update") && (
            <Tooltip title="Edit">
              <button
                onClick={() => handleEditClick(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
               shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
               hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
               active:translate-y-[1px] active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 ease-in-out group"
              >
                <FaPen size={12} />
              </button>
            </Tooltip>
          )}

          {/* 🗑️ Soft Delete */}
          {hasPermission("developers", "delete") && (
            <Tooltip title="Trash">
              <button
                onClick={() => handleDeleteConfirmation(record._id)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
              >
                <RiInboxArchiveLine size={18} />
              </button>
            </Tooltip>
          )}
        </div>
      );
    },
  });

  const activeColumns = [
    ...columns.filter((col) => col.dataIndex !== "action"),
    getActionColumn(false),
  ];

  const deletedColumns = [
    ...columns.filter((col) => col.dataIndex !== "action"),
    getActionColumn(true),
  ];

  const handleRestoreDeveloper = async () => {
    if (restoreId) {
      try {
        const formData = new FormData();

        formData.append("id", restoreId);
        formData.append("isDeleted", "false");

        await updateDeveloper(formData);

        message.success("Developer restored successfully");
        fetchDevelopers();
        fetchDeletedDevelopers();
      } catch (error) {
        console.error("Restore error:", error);
        message.error("Failed to restrore developer. Please try again.");
      } finally {
        setRestoreId(null);
      }
    }
  };

  return (
    <div>
      {/* Header & Search */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="overflow-x-auto">
            <div
              className="flex w-fit"
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {[
                { key: "1", label: "Teams", count: totalRecords },
                { key: "2", label: "Archived", count: deletedTotal },
              ].map((tab, index) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    if (tab.key === "1") setPage(1);
                    if (tab.key === "2") setDeletedPage(1);
                  }}
                  style={{
                    borderRight: index < 1 ? "1px solid #e5e7eb" : "none",
                  }}
                  className={
                    "flex shrink-0 items-center gap-2 px-6 py-2.5 text-[15px] font-medium whitespace-nowrap outline-none transition-colors " +
                    (activeTab === tab.key
                      ? "bg-[#001529] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50")
                  }
                >
                  {tab.label}
                  <span
                    className={
                      "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold leading-none " +
                      (activeTab === tab.key
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 text-gray-600")
                    }
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <Card
            hoverable
            style={{
              borderRadius: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              padding: "2px 8px",
              display: "flex",
              height: "38px",
              alignItems: "center",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
                fontWeight: 500,
                color: "#001529",
              }}
            >
              <RiTeamFill style={{ fontSize: "20px", color: "#001529" }} />
              <span>Total:</span>
              <span style={{ fontSize: "18px", fontWeight: 600 }}>
                {activeTab === "1" ? totalRecords : deletedTotal}
              </span>
            </div>
          </Card>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
                setDeletedPage(1);
              }}
              style={{ width: 220, height: "33px" }}
            />
            <Tooltip title="Sync from Website">
              <Button type="primary" className="allbuttons">
                <RiRefreshLine className="button-icon" />
              </Button>
            </Tooltip>
            <Button
              type="primary"
              onClick={() => setIsAddTaskModalOpen(true)}
              className="allbuttons"
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>
            <AddDeveloperModal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onTaskCreated={() => {
                setIsAddTaskModalOpen(false);
                fetchDevelopers();
              }}
            />
          </div>
        </div>
      </div>
      <div className="p-6 pt-0">
        {activeTab === "1" && (
          <Spin spinning={loading}>
            <div
              className="scrollbar-hide custom-scroll-table"
              style={{ overflowX: "auto" }}
            >
              <Table
                columns={activeColumns}
                dataSource={dataSource}
                rowKey="_id"
                scroll={{ x: "max-content" }}
                pagination={{
                  pageSize: 10,
                  current: page,
                  onChange: (pageNumber) => setPage(pageNumber),
                  showSizeChanger: false,
                  total: totalRecords,
                }}
              />
            </div>
          </Spin>
        )}
        {activeTab === "2" && (
          <Spin spinning={deletedLoading}>
            <div
              className="scrollbar-hide custom-scroll-table"
              style={{ overflowX: "auto" }}
            >
              <Table
                columns={deletedColumns}
                dataSource={deletedDevelopers}
                rowKey="_id"
                scroll={{ x: "max-content" }}
                pagination={{
                  pageSize: 10,
                  current: deletedPage,
                  onChange: (pageNumber) => setDeletedPage(pageNumber),
                  showSizeChanger: false,
                  total: deletedTotal,
                }}
              />
            </div>
          </Spin>
        )}
      </div>

      {/* document */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
        className="custom-modal"
        style={{
          bottom: 20,
          top: 20,
          height: "60vh",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Documents
          </div>
        }
        bodyStyle={{
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
          height: "calc(100% - 55px)",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
      >
        <div className="elements">
          {(selectedRecord?.document ?? []).length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {(() => {
                const items: React.ReactNode[] = [];
                let filePair: Array<{
                  doc: string;
                  fileUrl: string;
                  index: number;
                  isImage: boolean;
                }> = [];

                selectedRecord?.document.forEach((doc, index) => {
                  const fileExtension = doc.split(".").pop()?.toLowerCase();
                  const isImage = [
                    "jpg",
                    "jpeg",
                    "png",
                    "gif",
                    "webp",
                  ].includes(fileExtension || "");
                  const isPDF = fileExtension === "pdf";
                  const isDoc = ["doc", "docx"].includes(fileExtension || "");
                  const fileUrl = `${IMAGEURL}${encodeURIComponent(doc)}`;

                  if (isImage || isPDF || isDoc) {
                    filePair.push({ doc, fileUrl, index, isImage });

                    if (
                      filePair.length === 2 ||
                      index === selectedRecord.document.length - 1
                    ) {
                      items.push(
                        <div
                          key={`file-group-${index}`}
                          style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            gap: "16px",
                            width: "100%",
                          }}
                        >
                          {filePair.map((file) => (
                            <div
                              key={`file-${file.index}`}
                              style={{
                                width: "50%",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                                boxSizing: "border-box",
                                height: "220px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                position: "relative", // Added for positioning delete icon
                              }}
                            >
                              {/* Delete icon */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  cursor: "pointer",
                                  background: "white",
                                  borderRadius: "50%",
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                  zIndex: 1,
                                }}
                                onClick={() =>
                                  handleDeleteDocument(file.doc, file.index)
                                }
                              >
                                <DeleteOutlined style={{ color: "red" }} />
                              </div>

                              {file.isImage ? (
                                <a
                                  href={file.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ width: "100%" }}
                                >
                                  <Image
                                    src={file.fileUrl}
                                    style={{
                                      width: "100%",
                                      height: "180px",
                                      objectFit: "contain",
                                      display: "block",
                                      borderRadius: "4px",
                                    }}
                                    preview={{
                                      toolbarRender: () => <></>,
                                    }}
                                  />
                                </a>
                              ) : (
                                <a
                                  href={file.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    height: "180px",
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textDecoration: "none",
                                  }}
                                >
                                  <FilePdfOutlined
                                    style={{ fontSize: 80, color: "#FF0000" }}
                                  />
                                  <span
                                    style={{
                                      marginTop: 10,
                                      fontSize: 14,
                                      color: "#666",
                                      textAlign: "center",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {file.doc}
                                  </span>
                                </a>
                              )}
                            </div>
                          ))}

                          {filePair.length === 1 && (
                            <div style={{ width: "50%" }} />
                          )}
                        </div>,
                      );
                      filePair = [];
                    }
                  }
                });

                return items;
              })()}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "200px",
                color: "rgba(0,0,0,0.45)",
              }}
            >
              <FileOutlined style={{ fontSize: "24px", marginRight: "8px" }} />
              No documents available
            </div>
          )}
        </div>
      </Modal>
      {/* Archive Confirmation Modal */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to move Developer to Archive?</p>
      </Modal>

      {/* Hard Delete Confirmation Modal */}
      <Modal
        open={!!hardDeleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleHardDelete}
        onCancel={() => setHardDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Developer?</p>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal
        open={!!restoreId} // <-- This makes the modal open when deleteId has a value
        onOk={handleRestoreDeveloper}
        onCancel={() => setRestoreId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to restore this Developer?</p>
      </Modal>

      <Modal
        width={600}
        centered={false} // Disable centered to use top positioning
        destroyOnClose
        className="custom-modal"
        visible={isPasswordModalVisible}
        onCancel={closePasswordModal}
        footer={null}
        style={{
          bottom: 20,
          top: 20,
          maxHeight: "calc(100vh - 3  0px)", // Leaves 20px space at bottom
          margin: "0 auto",
          position: "relative",
          // paddingBottom: 20 // Adds space inside modal at bottom
        }}
        bodyStyle={{
          overflowY: "auto",
          // padding: '20px',
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            {`Reset Password  ${selectedUser?.name || ""}`}
          </div>
        }
      >
        <Form
          form={form}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 18 }}
          colon={false}
          style={{ margin: 0 }}
          onFinish={(values) => onFinishPasswordChange(values, selectedUser)}
        >
          <div className="elements">
            <Form.Item
              required={false}
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  New Password <span className="text-danger">*</span>
                </span>
              }
              name="newPassword"
              rules={[{ required: true, message: "Please enter new password" }]}
            >
              <Input.Password className="Allinput" />
            </Form.Item>

            <Form.Item
              required={false}
              name="confirmPassword"
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Confirm Password <span className="text-danger">*</span>
                </span>
              }
              rules={[
                { required: true, message: "Please confirm new password" },
              ]}
            >
              <Input.Password className="Allinput" />
            </Form.Item>
          </div>
          <Row justify="end">
            <Col
              xs={24}
              sm={24}
              md={24}
              xl={24}
              xxl={20}
              className="flex justify-content-end me-3"
            >
              <div className="button-wrapper">
                <button
                  className="p-[10px 16px] w-[160px] h-[40px] rounded-[6px] bg-[#001529] text-[#fff] mb-[1rem] cursor-pointer"
                  type="submit"
                >
                  Update Password
                </button>
              </div>
            </Col>
          </Row>
          {/* <Button type="primary" htmlType="submit" loading={isUpdating} block>
            Update Password
          </Button> */}
        </Form>
      </Modal>

      {currentTask && (
        // In your table component where you render the modal:
        <EditDeveloperModal
          visible={isEditModalVisible}
          taskId={currentTask?._id} // Make sure this is passed
          onCancel={() => setIsEditModalVisible(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export const TEAMS_TABLE_COLUMNS = [
  "employeeId",
  "photo",
  "username",
  "designation",
  "email",
  "mobileNo",
  "dob",
];
